import { AlertTriangle, Inbox, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type ButtonHTMLAttributes, type HTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  fullWidth?: boolean
}

export function Button({
  variant = 'primary',
  fullWidth = false,
  className = '',
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      {...props}
      type={type}
      className={`button button--${variant}${fullWidth ? ' button--full' : ''} ${className}`}
    />
  )
}

export function Card({ children, className = '', ...props }: HTMLAttributes<HTMLElement> & { children: ReactNode }) {
  return <article {...props} className={`card ${className}`}>{children}</article>
}

type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string
  hint?: string | undefined
}

export function Field({ label, hint, id, className = '', ...props }: FieldProps) {
  const fieldId = id ?? props.name ?? label
  const hintId = hint ? `${fieldId}-hint` : undefined
  return (
    <div className={`field ${className}`}>
      <label className="field__label" htmlFor={fieldId}>{label}</label>
      <input {...props} aria-describedby={hintId} className="field__input" id={fieldId} />
      {hint ? <span className="field__hint" id={hintId}>{hint}</span> : null}
    </div>
  )
}

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'success' | 'warning' | 'danger' }) {
  return <span className={`badge badge--${tone}`}>{children}</span>
}

export function Sheet({
  title,
  children,
  onClose,
}: {
  title: string
  children: ReactNode
  onClose: () => void
}) {
  const [closing, setClosing] = useState(false)
  const closingRef = useRef(false)
  const closeTimer = useRef<number | null>(null)
  const requestClose = useCallback(() => {
    if (closingRef.current) return
    closingRef.current = true
    setClosing(true)
    closeTimer.current = window.setTimeout(onClose, 210)
  }, [onClose])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') requestClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current)
    }
  }, [requestClose])

  return createPortal(
    <div className={`sheet-backdrop${closing ? ' is-closing' : ''}`} role="presentation" onMouseDown={requestClose}>
      <section
        aria-labelledby="sheet-title"
        aria-modal="true"
        className="sheet"
        role="dialog"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="sheet__handle" aria-hidden="true" />
        <header className="sheet__header">
          <h2 id="sheet-title">{title}</h2>
          <Button variant="ghost" aria-label="Fermer" onClick={requestClose}>
            <X size={20} />
          </Button>
        </header>
        <div className="sheet__content">{children}</div>
      </section>
    </div>,
    document.body,
  )
}

export function EmptyState({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="state-card">
      <Inbox size={24} aria-hidden="true" />
      <strong>{title}</strong>
      <p>{detail}</p>
    </div>
  )
}

export function ErrorState({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="state-card state-card--error" role="alert">
      <AlertTriangle size={24} aria-hidden="true" />
      <strong>{title}</strong>
      <p>{detail}</p>
    </div>
  )
}
